// SPDX-FileCopyrightText: 2025 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import LabelMetaDataProvider from './label/LabelMetaDataProvider';
import ImagesList from './label/ImagesList';
import ImageListOrderProvider from './label/images/ImageListOrderProvider';
import AlgorithmsImage from './algorithms/AlgorithmsImage';
import {
  AlgorithmProgrammingSidebar,
  prepareQCProgramForSave,
  QCPContext,
  AlgorithmProgrammingContext,
} from '@tls/sw91_ui_templates';
import { AlgorithmProgrammingFooter } from './algorithms/AlgorithmsProgrammingFooter';
import useProjectLabels from 'hooks/useProjectLabels';
import { QCProgram, DataLoggerParameter } from '@tls/sw91_ui_templates/lib/models/project-models';
import useQCPApi from 'hooks/useQCPApi';
import { useParams } from 'react-router-dom';
import ParamsWithId from 'model/ParamsWithId';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import { mapModelClassesToLabelClasses } from './evaluate/algorithms/utils';
import { useTranslation } from 'react-i18next';
import { Colors, LoaderOverlay } from '@tls/treact-ui';
import TestableLoader from 'components/treactui-template/atoms/TestableLoader';
export default function Algorithms() {
  const { labels } = useProjectLabels();
  const [isEdit, setIsEdit] = useState(false);
  const { id } = useParams<ParamsWithId>();
  const { selectedProject } = useProjectsMetadata(id);
  const { t } = useTranslation();

  const modelClassesWithoutBackground = useMemo(
    () => selectedProject?.projectUseCase?.modelClasses?.filter(modelClass => modelClass.classIndex !== 0),
    [selectedProject?.projectUseCase?.modelClasses]
  );

  const { selectedMd5 } = useProjectLabels();
  const [algorithmImages, setAlgorithmImages] = useState<
    {
      base64JpegImage: string;
      imageName: string;
    }[]
  >([]);
  const [outputData, setOutputData] = useState<Record<string, DataLoggerParameter> | undefined>({});
  const { plugins } = useContext(QCPContext);
  const { computeQCP } = useQCPApi();
  const [strategyVersion, setStrategyVersion] = useState<'model' | 'label'>('model');
  const [selectedAlgorithmIndex, setSelectedAlgorithmIndex] = useState<number>();
  const [isComputeLoading, setIsComputeLoading] = useState(false);
  const [segmentationImage, setSegmentationImage] = useState<{
    base64JpegImage: string;
    imageName: string;
  }>();

  useEffect(() => {
    setOutputData({});
    setAlgorithmImages([]);
    setSegmentationImage(undefined);
  }, [selectedMd5]);

  const { configuredProject } = useContext(AlgorithmProgrammingContext);

  const handleCompute = useCallback(
    async (qcp: QCProgram) => {
      if (!id) return;
      if (qcp) {
        const preparedProgram: QCProgram = prepareQCProgramForSave(qcp, plugins);
        setIsComputeLoading(true);
        setOutputData(undefined);
        computeQCP(id, selectedMd5!, strategyVersion === 'model' ? 'WORKING_COPY' : 'MASK', preparedProgram)
          .then(data => {
            setOutputData(data.dataLoggerOutput);
            setAlgorithmImages(data.algoResultImages);
            setSegmentationImage(data.segmentationResultImage);
            setIsComputeLoading(false);
          })
          .catch(() => {
            console.error('Error during calculation');
            setIsComputeLoading(false);
          });
      }
    },
    [plugins, computeQCP, id, selectedMd5, strategyVersion]
  );
  const actualImage = useMemo(() => {
    if (!algorithmImages) return undefined;
    if (selectedAlgorithmIndex === undefined || selectedAlgorithmIndex < 0) return segmentationImage;
    const algortihmName =
      configuredProject?.instructions.stages.stage_03_calling_algorithms.algorithm_calls[selectedAlgorithmIndex]?.name;
    const image = algorithmImages.find(img => {
      return img.imageName.startsWith(`algorithm_calls.${algortihmName ?? 'NO*NAME*FOUND'}`);
    });
    if (!image) {
      return undefined;
    }
    return image;
  }, [algorithmImages, selectedAlgorithmIndex, configuredProject, segmentationImage]);

  const hasSelectedImageAMask = useMemo(() => {
    const label = labels.find(label => label.imageMd5 === selectedMd5);
    return label?.hasMask ?? false;
  }, [labels, selectedMd5]);

  return (
    <Root>
      <LabelMetaDataProvider>
        <ImagesContainer>
          <ImageListOrderProvider>
            <ListContainer>
              <ImagesList withoutKeyhandlers />
            </ListContainer>
            <ImageContainer>
              <AlgorithmsImage imageBase64={actualImage?.base64JpegImage} />
            </ImageContainer>
          </ImageListOrderProvider>
        </ImagesContainer>
      </LabelMetaDataProvider>
      <RightSideContainer>
        <RightSideContent>
          {isComputeLoading && (
            <LoaderOverlay>
              <TestableLoader label={t('project.algorithms.computing')} data-testid='algorithms-compute-loader' />
            </LoaderOverlay>
          )}
          {!isComputeLoading && (
            <AlgorithmProgrammingSidebar
              isComputeLoading={isComputeLoading}
              selectedAlgorithmIndex={selectedAlgorithmIndex}
              setSelectedAlgorithmIndex={setSelectedAlgorithmIndex}
              imageMd5={selectedMd5}
              translationPrefix='project.algorithms'
              isEdit={isEdit}
              computeOn={strategyVersion}
              setComputeOn={setStrategyVersion}
              labelClasses={mapModelClassesToLabelClasses(modelClassesWithoutBackground)}
              withEditSwitch
              computeAll={handleCompute}
              onEditButtonClick={() => setIsEdit(true)}
              outputData={outputData}
              restrictComputeOnLabel={!hasSelectedImageAMask}
            />
          )}
        </RightSideContent>
        {isEdit && <AlgorithmProgrammingFooter setIsEdit={setIsEdit} />}
      </RightSideContainer>
    </Root>
  );
}

const Root = styled.div`
  display: grid;
  grid-template-areas: 'image right-side';
  grid-template-columns: auto 36rem;
  gap: 0.5rem;
  height: calc(100% - 1rem);
  margin: 0.5rem;
  min-width: 1172px;
`;

const ListContainer = styled.div`
  grid-area: list;
  position: relative;
  border-right: 1px solid ${Colors.trgrey2.hex};
`;

const ImageContainer = styled.div`
  grid-area: image;
`;

const ImagesContainer = styled.div`
  grid-area: image;
  display: grid;
  grid-template: 'list image';
  grid-template-columns: 26rem auto;
  grid-template-rows: 100%;
  border: 1px solid ${Colors.trgrey2.hex};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  overflow: hidden;
`;
const RightSideContainer = styled.div`
  grid-area: right-side;
  height: calc(100% - 2px);
  border: 1px solid ${Colors.trgrey2.hex};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const RightSideContent = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
`;
